import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Button,
    InputField,
    useAuthentication,
    useFormErrors,
    useLoading,
    useNotifications,
} from '@freelancelabs/components';
import { emailValidator, requiredValidator } from '@freelancelabs/shared';

import { PublicRoutes } from '../../constants';

interface Props {
    defaultEmail?: string;
}

const ResetPasswordForm = ({ defaultEmail }: Props) => {
    const { forgotPassword } = useAuthentication() as AuthenticationStore;
    const { validator, onFormSubmit } = useFormErrors();
    const [submitting, withSubmitting] = useLoading();
    const [username, setUsername] = useState(defaultEmail || '');
    const { createNotification } = useNotifications();
    const navigate = useNavigate();

    const handleSubmit = () => {
        if (submitting || !onFormSubmit()) {
            return;
        }
        const run = async () => {
            await forgotPassword(username);
            return navigate(PublicRoutes.EMAIL_SENT, { state: { username } });
        };

        withSubmitting(run()).catch((e) => {
            // Amplify v6 puts the Cognito error name on e.name (e.code was v5).
            const code = e?.name || e?.code;
            switch (code) {
                case 'LimitExceededException':
                    createNotification({
                        type: 'danger',
                        text: c('Reset Password Error').t`Attempt limit exceeded`,
                    });
                    break;
                case 'InvalidParameterException':
                    // Cognito refuses email reset when the email is already a second factor.
                    createNotification({
                        type: 'danger',
                        text: c('Reset Password Error')
                            .t`If email two-factor authentication is enabled on your account, sign in with the magic link then temporarily disable it from My account.`,
                    });
                    break;
                default:
                    createNotification({
                        type: 'danger',
                        text: c('Reset Password Error').t`Unknown error`,
                    });
            }
        });
    };

    return (
        <>
            <form
                name="registerForm"
                className="flex flex-col"
                onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                }}
                method="post"
            >
                <InputField
                    label={c('Info').t`Email address`}
                    id="username"
                    error={validator([requiredValidator(username), emailValidator(username)])}
                    disableChange={submitting}
                    autoComplete="username"
                    type="email"
                    value={username}
                    onValue={setUsername}
                />
                <Button size="large" loading={submitting}>{c('Action').t`Send`}</Button>
            </form>
            <hr className="my-6" />
            <div className="text-center text-sm font-medium">
                <Link className="link" to={PublicRoutes.LOGIN}>
                    {c('Link').t`Back to sign in`}
                </Link>
            </div>
        </>
    );
};

export default ResetPasswordForm;
